import { ArrowLeftRight } from 'lucide-react'
import { Cell, Pie, PieChart, type PieLabelRenderProps } from 'recharts'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { DeviceBreakdown } from '@/types/analytics'

export type DeviceUsageMetric = 'loads' | 'messages' | 'redirects'

const metricLabels: Record<DeviceUsageMetric, string> = {
  loads: 'Loads',
  messages: 'Messages',
  redirects: 'Redirects',
}

const metricSubtitles: Record<DeviceUsageMetric, string> = {
  loads: 'Percent of loads by device type',
  messages: 'Percent of messages by device type',
  redirects: 'Percent of redirects by device type',
}

interface DeviceUsageChartProps {
  data: DeviceBreakdown[]
  selectedMetric?: DeviceUsageMetric
  onMetricChange?: (metric: DeviceUsageMetric) => void
  isFullScreen?: boolean
}

// Hardcoded device colors - these never change regardless of data order
const DEVICE_COLORS: Record<string, string> = {
  mobile: 'hsl(var(--chart-1))',
  desktop: 'hsl(var(--chart-2))',
  tablet: 'hsl(var(--chart-3))',
  unknown: 'hsl(var(--chart-4))',
}

const DEVICE_LABELS: Record<string, string> = {
  mobile: 'Mobile',
  desktop: 'Desktop',
  tablet: 'Tablet',
  unknown: 'Unknown',
}

const DEFAULT_COLOR = 'hsl(var(--chart-4))'

// recharts 3 spreads the datum onto the Pie sector under `payload`; the custom
// `label` field we attach in chartData lives there. Read it without an `as`.
function readLabel(payload: unknown): string {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'label' in payload &&
    typeof payload.label === 'string'
  ) {
    return payload.label
  }

  return ''
}

const chartConfig = {
  mobile: {
    label: 'Mobile',
    color: DEVICE_COLORS.mobile,
  },
  desktop: {
    label: 'Desktop',
    color: DEVICE_COLORS.desktop,
  },
  tablet: {
    label: 'Tablet',
    color: DEVICE_COLORS.tablet,
  },
  unknown: {
    label: 'Unknown',
    color: DEVICE_COLORS.unknown,
  },
} satisfies ChartConfig

const chartClassNames = {
  card: 'h-full rounded-2xl shadow-sm',
  header: 'pb-2',
  title: 'text-base font-semibold leading-4',
  subtitle: 'text-sm text-muted-foreground',
  content: 'pt-0',
}

// Fixed order for devices to ensure consistent rendering
const DEVICE_ORDER = ['mobile', 'desktop', 'tablet', 'unknown']

export function DeviceUsageChart({
  data,
  selectedMetric = 'loads',
  onMetricChange,
  isFullScreen = false,
}: DeviceUsageChartProps) {
  const resolvedMetricLabels: Record<DeviceUsageMetric, string> = isFullScreen
    ? { ...metricLabels, loads: 'Views' }
    : metricLabels

  const resolvedMetricSubtitles: Record<DeviceUsageMetric, string> =
    isFullScreen
      ? { ...metricSubtitles, loads: 'Percent of views by device type' }
      : metricSubtitles
  // Map data to include colors based on device type using hardcoded map
  // Sort by fixed device order to ensure consistent chart segment positions
  const chartData = data
    .map((item) => {
      const deviceKey = item.device.toLowerCase()
      return {
        ...item,
        fill: DEVICE_COLORS[deviceKey] ?? DEFAULT_COLOR,
        label: DEVICE_LABELS[deviceKey] ?? item.device,
        sortOrder: DEVICE_ORDER.indexOf(deviceKey),
      }
    })
    .sort((a, b) => {
      // Unknown devices (-1 from indexOf) go to the end
      const orderA = a.sortOrder === -1 ? 999 : a.sortOrder
      const orderB = b.sortOrder === -1 ? 999 : b.sortOrder
      return orderA - orderB
    })

  const metrics: DeviceUsageMetric[] = ['loads', 'messages', 'redirects']

  return (
    <Card className={chartClassNames.card}>
      <CardHeader className={chartClassNames.header}>
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1.5">
            <CardTitle className={chartClassNames.title}>
              Device Usage
            </CardTitle>
            <p className={chartClassNames.subtitle}>
              {resolvedMetricSubtitles[selectedMetric]}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <ArrowLeftRight className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {metrics.map((metric) => (
                <DropdownMenuCheckboxItem
                  key={metric}
                  checked={selectedMetric === metric}
                  onCheckedChange={() => onMetricChange?.(metric)}
                >
                  {resolvedMetricLabels[metric]}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className={chartClassNames.content}>
        <div className="flex flex-col items-center gap-9">
          <ChartContainer config={chartConfig} className="h-[260px] w-[260px]">
            <PieChart accessibilityLayer>
              <ChartTooltip content={<ChartTooltipContent />} />
              <Pie
                data={chartData}
                dataKey="count"
                nameKey="device"
                cx="50%"
                cy="50%"
                outerRadius={130}
                label={({
                  cx,
                  cy,
                  midAngle,
                  innerRadius,
                  outerRadius,
                  payload,
                }: PieLabelRenderProps) => {
                  // recharts 3: PieLabelRenderProps has no custom `label` field;
                  // the datum is spread onto the sector under `payload`, so read
                  // the original `label` from there. Coordinates are nullable.
                  if (cx == null || cy == null) return null
                  const RADIAN = Math.PI / 180
                  const angle = midAngle ?? 0
                  const inner = Number(innerRadius ?? 0)
                  const outer = Number(outerRadius ?? 0)
                  const radius = inner + (outer - inner) * 0.5
                  const x = Number(cx) + radius * Math.cos(-angle * RADIAN)
                  const y = Number(cy) + radius * Math.sin(-angle * RADIAN)
                  return (
                    <text
                      x={x}
                      y={y}
                      fill="white"
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="text-xs font-normal"
                    >
                      {readLabel(payload)}
                    </text>
                  )
                }}
                labelLine={false}
              >
                {chartData.map((entry) => (
                  <Cell key={`cell-${entry.device}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>
          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            {chartData.map((item) => (
              <div key={item.device} className="flex items-center gap-1.5">
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: item.fill }}
                />
                <span className="text-xs text-foreground">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
